import { Switch, ToggleButton, theme, useToast, DeprecatedCheckIcon } from '@arnold/common';
import styled from '@emotion/styled';
import { uniq } from 'ramda';
import { useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  NotificationLevel,
  OnePlaceAuthInput,
  UserOrgRole,
  useSetRespondentNewAnswersNotificationsMutation,
  useSetUserSummaryNotificationsMutation,
} from '../generated/hooks';
import { OverviewProps } from './Overview';

export const OverviewSettings = ({ respondents, user, auth }: Omit<OverviewProps, 'baseUrl'>) => {
  const [setUserSummaryNotificationsMutation] = useSetUserSummaryNotificationsMutation();
  const { t } = useTranslation('overviewSettings');
  const { addToast } = useToast();
  const [summaryNotifications, setSummaryNotifications] = useState(user.summaryNotifications);

  return (
    <StyledCard>
      <SettingsForUserTitle>{t('notificationSettings')}</SettingsForUserTitle>
      <SettingsForUserEmail>
        {t('myAccount')} {user.username}
      </SettingsForUserEmail>
      <Divider />
      <SummaryEmailSettings data-icom="settings-summary-email">
        <SummaryHorizontalLayout>
          <div>{t('sendSummaryEmail')}</div>
          <ToggleButton
            value={summaryNotifications}
            onChange={(value) => {
              setSummaryNotifications(value);
              setUserSummaryNotificationsMutation({
                variables: {
                  on: value,
                  auth,
                },
                onCompleted: () => {
                  addToast(t('notificationSettingsSaved'), 2000);
                },
                onError: () => {
                  addToast(t('notificationSettingsError'));
                },
              });
            }}
          />
        </SummaryHorizontalLayout>
        <SummaryEmailSettingsDescription>
          <div>
            <DeprecatedCheckIcon /> <span>{t('sendSummaryEmailDescription1')}</span>
          </div>
          <div>
            <DeprecatedCheckIcon /> <span>{t('sendSummaryEmailDescription2')}</span>
          </div>
          <div>
            <DeprecatedCheckIcon /> <span>{t('sendSummaryEmailDescription3')}</span>
          </div>
        </SummaryEmailSettingsDescription>
      </SummaryEmailSettings>
      <Divider />
      <SummaryEmailDetailedSettings data-icom="settings-notifications">
        <DetailedSettingsTitle>{t('sendSummaryFromOrganizations')}</DetailedSettingsTitle>
        <DetailedSettingsDescription>{t('sendSummaryFromOrganizationsDescription')}</DetailedSettingsDescription>
        <DetailedSettingsList>
          <DetailedSettingsListItem>
            <ListItemHeader>{t('organizationNames')}</ListItemHeader>
            <ListItemHeader>{t('sendNotificationsForNewAnswers')}</ListItemHeader>
          </DetailedSettingsListItem>
          {respondents
            .filter((r) => r.organizationRole === UserOrgRole.OrgAdmin || r.isLeader || r.isReportRecipient)
            .map((respondent, i) => {
              let topics = uniq(
                respondent.reports
                  ?.slice()
                  .sort(
                    (a, b) =>
                      new Date(b.survey?.endsAt || b.surveyGroup?.to).getTime() -
                      new Date(a.survey?.endsAt || a.surveyGroup?.to).getTime(),
                  )
                  .map((r) => r.topicName) ?? [],
              )
                .slice(0, 3)
                .join(', ');
              if (topics.length < 7) {
                topics = t('noSurveys');
              }
              let value = respondent.newAnswersNotifications;
              if (value == null) {
                value = respondent.organization.processNotificationsDefault;
              }
              const forcedSetting = respondent.organization.processNotificationsDefault;
              const forcedSettingText = t(`${forcedSetting}ByAdmin`);
              return (
                <DetailedSettingsListItem key={i}>
                  <VerticalLayout>
                    <OrgName>{respondent.organization.name}</OrgName>
                    <OrgSurveys>{topics}</OrgSurveys>
                  </VerticalLayout>
                  {!respondent.organization.processNotificationsLocked ? (
                    <NewAnswersNotificationSetting respondentId={respondent.id} auth={auth} initialValue={value} />
                  ) : (
                    <ForcedSetting>{forcedSettingText}</ForcedSetting>
                  )}
                </DetailedSettingsListItem>
              );
            })}
        </DetailedSettingsList>
      </SummaryEmailDetailedSettings>
    </StyledCard>
  );
};

const NewAnswersNotificationSetting = ({
  respondentId,
  auth,
  initialValue,
}: {
  respondentId: string;
  auth?: OnePlaceAuthInput;
  initialValue: NotificationLevel;
}) => {
  const [setRespondentNewAnswersNotifications] = useSetRespondentNewAnswersNotificationsMutation();
  const [value, setValue] = useState<NotificationLevel>(initialValue);
  const { t } = useTranslation('overviewSettings');
  const { addToast } = useToast();
  return (
    <Switch
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        setRespondentNewAnswersNotifications({
          variables: {
            respondentId,
            notificationLevel: newValue,
            auth,
          },
          onCompleted: () => {
            addToast(t('notificationSettingsSaved'), 2000);
          },
          onError: () => {
            addToast(t('notificationSettingsError'));
          },
        });
      }}
      possibleValues={[NotificationLevel.Never, NotificationLevel.AlertsOnly, NotificationLevel.All].map((value) => ({
        value,
        label: t(value),
      }))}
    />
  );
};

const StyledCard = styled(Card)`
  margin-top: ${theme.spacing.h};
  padding: ${theme.spacing.i};
  @media (max-width: ${theme.breakpoint.small}) {
    padding: ${theme.spacing.f};
  }
`;

const SettingsForUserTitle = styled.h2`
  margin-top: ${theme.spacing.d};
  font-weight: bold;
`;

const SettingsForUserEmail = styled.span`
  color: ${theme.colors.text.secondary};
`;

const Divider = styled.div`
  border-top: 1px solid ${theme.colors.borderMain.default};
  margin-top: ${theme.spacing.h};
  width: 100%;
`;

const SummaryEmailSettings = styled.div`
  margin-top: ${theme.spacing.h};
`;

const SummaryHorizontalLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 400;
  color: ${theme.colors.text.primary};
`;

const SummaryEmailSettingsDescription = styled.div`
  margin-left: ${theme.spacing.c};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.small.regular.fontSize};
  & div {
    margin-bottom: ${theme.spacing.c};
  }
  & span {
    margin-left: ${theme.spacing.d};
  }
  & svg > path {
    fill: ${theme.colors.emotionSuccess.default};
  }
`;

const SummaryEmailDetailedSettings = styled.div`
  margin-top: ${theme.spacing.h};
`;

const DetailedSettingsTitle = styled.h3`
  font-weight: 400;
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.d};
`;

const DetailedSettingsDescription = styled.div`
  color: ${theme.colors.text.secondary};
`;

const DetailedSettingsList = styled.div`
  margin-top: ${theme.spacing.f};
`;

const DetailedSettingsListItem = styled.div`
  width: 100%;
  background-color: ${theme.colors.backgroundBasic.default};
  border-radius: ${theme.borderRadius.s};
  margin: ${theme.spacing.c};
  padding: ${theme.spacing.e};
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.b};
  & > * {
    margin: ${theme.spacing.c};
    min-width: 300px;
    width: 45%;
  }
`;

const ListItemHeader = styled.div`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.body.small.regular.fontSize};
`;

const VerticalLayout = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrgName = styled.div`
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.c};
`;

const OrgSurveys = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.small.regular.fontSize};
`;

const ForcedSetting = styled.div`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.body.small.regular.fontSize};
`;
